import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  FormHelperText,
  Toolbar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  CheckCircle,
  Cancel,
  Refresh,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fleetServiceTemplateApi } from '../services/api';
import { FleetServiceTemplate, FleetServiceTemplateCreateRequest, FleetServiceTemplateUpdateRequest } from '../types/fleetServiceTemplate';
import { ServiceDueRuleType, ServiceTaskType } from '../types/vehicleMaintenance';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { enumLabel } from '../i18n/translate';
import { TableFilterCell } from '../components/TableFilterCell';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';


const emptyFormData: FleetServiceTemplateCreateRequest = {
  serviceType: ServiceTaskType.OIL_CHANGE,
  templateName: '',
  description: '',
  dueRuleType: ServiceDueRuleType.MILEAGE,
  recurrenceKilometersKm: undefined,
  recurrenceMonths: undefined,
  appliesToAllVehicles: false,
  isActive: true,
};

export const FleetServiceTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FleetServiceTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [columnFilters, setColumnFilters] = useState({
    templateName: '',
    serviceType: '',
    dueRuleType: '',
    appliesToAllVehicles: '',
    status: '',
  });

  const queryClient = useQueryClient();

  const { data: templates = [], isLoading } = useQuery('fleetServiceTemplates', fleetServiceTemplateApi.getAll);

  const createMutation = useMutation(fleetServiceTemplateApi.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('fleetServiceTemplates');
      toast.success(t('fleetServiceTemplates.messages.created'));
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('fleetServiceTemplates.messages.createFailed'));
    },
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: FleetServiceTemplateUpdateRequest }) => fleetServiceTemplateApi.update(id, data),
    {
      onSuccess: (updatedTemplate) => {
        queryClient.invalidateQueries('fleetServiceTemplates');
        toast.success(t('fleetServiceTemplates.messages.updated'));
        loadTemplateIntoEditor(updatedTemplate);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('fleetServiceTemplates.messages.updateFailed'));
      },
    }
  );

  const deleteMutation = useMutation(fleetServiceTemplateApi.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('fleetServiceTemplates');
      toast.success(t('fleetServiceTemplates.messages.deleted'));
      setOpenDeleteDialog(false);
      setSelectedTemplate(null);
      setEditMode(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('fleetServiceTemplates.messages.deleteFailed'));
    },
  });

  const [formData, setFormData] = useState<FleetServiceTemplateCreateRequest>(emptyFormData);

  const loadTemplateIntoEditor = (template: FleetServiceTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      serviceType: template.serviceType,
      templateName: template.templateName,
      description: template.description || '',
      dueRuleType: template.dueRuleType,
      recurrenceKilometersKm: template.recurrenceKilometersKm ?? undefined,
      recurrenceMonths: template.recurrenceMonths ?? undefined,
      appliesToAllVehicles: template.appliesToAllVehicles,
      isActive: template.isActive,
    });
    setIsCreating(false);
    setEditMode(true);
  };

  const resetForm = () => {
    setFormData(emptyFormData);
    setSelectedTemplate(null);
    setIsCreating(false);
    setEditMode(false);
  };

  const handleNewTemplate = () => {
    resetForm();
    setIsCreating(true);
    setEditMode(true);
    setSelectedTemplate(null);
  };

  const handleRowClick = (template: FleetServiceTemplate) => {
    if (selectedTemplate?.id === template.id && !isCreating) {
      return;
    }
    loadTemplateIntoEditor(template);
  };

  const handleDelete = () => {
    if (selectedTemplate) {
      setOpenDeleteDialog(true);
    }
  };

  const handleConfirmDelete = () => {
    if (selectedTemplate) {
      deleteMutation.mutate(selectedTemplate.id.toString());
    }
  };

  const handleSave = () => {
    if (!formData.templateName) {
      toast.error(t('fleetServiceTemplates.validation.nameRequired'));
      return;
    }

    if (isCreating) {
      createMutation.mutate(formData);
    } else if (editMode && selectedTemplate) {
      updateMutation.mutate({ id: selectedTemplate.id.toString(), data: formData });
    }
  };

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const needsMileage = formData.dueRuleType !== ServiceDueRuleType.CALENDAR;
  const needsCalendar = formData.dueRuleType !== ServiceDueRuleType.MILEAGE;

  const filteredTemplates = templates.filter((template) => {
    const statusText = template.isActive ? 'active' : 'inactive';
    const defaultText = template.appliesToAllVehicles ? 'yes' : 'no';

    return (
      (!columnFilters.templateName || template.templateName.toLowerCase().includes(columnFilters.templateName.toLowerCase())) &&
      (!columnFilters.serviceType || template.serviceType.toLowerCase().includes(columnFilters.serviceType.toLowerCase())) &&
      (!columnFilters.dueRuleType || template.dueRuleType.toLowerCase().includes(columnFilters.dueRuleType.toLowerCase())) &&
      (!columnFilters.appliesToAllVehicles || defaultText.includes(columnFilters.appliesToAllVehicles.toLowerCase())) &&
      (!columnFilters.status || statusText.includes(columnFilters.status.toLowerCase()))
    );
  });

  const paginatedTemplates = filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          width: '100%'
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewTemplate}
                color="primary"
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >{t('common.new')}</Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!isCreating && !editMode}
                color="primary"
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >{t('common.save')}</Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={!selectedTemplate}
                color="error"
                size="small"
                sx={{ minWidth: 'auto', px: 2, py: 0.75, textTransform: 'none', fontSize: '0.875rem' }}
              >{t('common.delete')}</Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Toolbar sx={{
                pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5,
                bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh fontSize="small" />}
                  onClick={() => queryClient.invalidateQueries('fleetServiceTemplates')}
                  size="small"
                  sx={{ minWidth: 'auto', px: 1.5, py: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
                >{t('common.refresh')}</Button>
              </Toolbar>

              <TableContainer>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { fontSize: '0.875rem', px: { xs: 2, sm: 3 } } }}>
                  <TableHead>
                    <TableRow>
                      <TableFilterCell placeholder={t('fields.templateName')} value={columnFilters.templateName} onChange={(value) => handleColumnFilterChange('templateName', value)} width="220px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                      <TableFilterCell placeholder={t('fields.type')} value={columnFilters.serviceType} onChange={(value) => handleColumnFilterChange('serviceType', value)} width="150px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                      <TableFilterCell placeholder={t('fields.dueRule')} value={columnFilters.dueRuleType} onChange={(value) => handleColumnFilterChange('dueRuleType', value)} width="180px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                      <TableCell sx={{ width: '150px', fontWeight: 600 }}>{t('fields.recurrenceKm')} / {t('fields.recurrenceMonths')}</TableCell>
                      <TableFilterCell placeholder={t('fields.defaultForFleet')} value={columnFilters.appliesToAllVehicles} onChange={(value) => handleColumnFilterChange('appliesToAllVehicles', value)} width="140px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                      <TableFilterCell placeholder={t('fields.status')} value={columnFilters.status} onChange={(value) => handleColumnFilterChange('status', value)} width="120px" cellSx={{ px: 0.75, borderBottom: '1px solid', borderColor: 'divider' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                          {t('fleetServiceTemplates.emptyState')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTemplates.map((template) => (
                        <TableRow
                          key={template.id}
                          hover
                          selected={selectedTemplate?.id === template.id}
                          onClick={() => handleRowClick(template)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ py: 0.75 }}>{template.templateName}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>{enumLabel(t, 'serviceTaskTypes', template.serviceType)}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>{enumLabel(t, 'dueRuleTypes', template.dueRuleType)}</TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            {[
                              template.recurrenceKilometersKm ? `${template.recurrenceKilometersKm.toLocaleString()} km` : null,
                              template.recurrenceMonths ? t('fleetServiceTemplates.monthsAbbrev', { count: template.recurrenceMonths, defaultValue: `${template.recurrenceMonths} mo` }) : null,
                            ].filter(Boolean).join(' / ') || '-'}
                          </TableCell>
                          <TableCell sx={{ py: 0.75, textAlign: 'center' }}>
                            {template.appliesToAllVehicles ? (
                              <Chip size="small" label={t('fields.defaultForFleet')} color="primary" />
                            ) : (
                              <Cancel color="disabled" fontSize="small" />
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Chip
                              label={template.isActive ? t('status.active') : t('status.inactive')}
                              size="small"
                              color={template.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedTemplate) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 1, display: 'flex', flexDirection: 'column', ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">
                    {isCreating ? t('fleetServiceTemplates.newTemplate') : selectedTemplate?.templateName || ''}
                  </Typography>
                  {selectedTemplate && !isCreating && (
                    <Chip
                      label={selectedTemplate.isActive ? t('status.active') : t('status.inactive')}
                      size="small"
                      color={selectedTemplate.isActive ? 'success' : 'default'}
                    />
                  )}
                </Box>
                <Divider sx={{ mb: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>{t('fields.type')}</InputLabel>
                      <Select
                        value={formData.serviceType}
                        label={t('fields.type')}
                        onChange={(e) => setFormData({ ...formData, serviceType: e.target.value as ServiceTaskType })}
                      >
                        {Object.values(ServiceTaskType).map((value) => (
                          <MenuItem key={value} value={value}>{enumLabel(t, 'serviceTaskTypes', value)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('fields.templateName')}
                      value={formData.templateName}
                      onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                      required
                      placeholder={t('fleetServiceTemplates.examplePlaceholder')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>{t('fields.dueRule')}</InputLabel>
                      <Select
                        value={formData.dueRuleType}
                        label={t('fields.dueRule')}
                        onChange={(e) => setFormData({ ...formData, dueRuleType: e.target.value as ServiceDueRuleType })}
                      >
                        {Object.values(ServiceDueRuleType).map((value) => (
                          <MenuItem key={value} value={value}>{enumLabel(t, 'dueRuleTypes', value)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {needsMileage && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.recurrenceKm')}
                        type="number"
                        value={formData.recurrenceKilometersKm ?? ''}
                        onChange={(e) => setFormData({ ...formData, recurrenceKilometersKm: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                        required
                      />
                    </Grid>
                  )}
                  {needsCalendar && (
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('fields.recurrenceMonths')}
                        type="number"
                        value={formData.recurrenceMonths ?? ''}
                        onChange={(e) => setFormData({ ...formData, recurrenceMonths: e.target.value ? parseInt(e.target.value, 10) : undefined })}
                        required
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('fields.description')}
                      multiline
                      minRows={1}
                      maxRows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                        />
                      }
                      label={t('status.active')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formData.appliesToAllVehicles}
                            onChange={(e) => setFormData({ ...formData, appliesToAllVehicles: e.target.checked })}
                          />
                        }
                        label={t('fields.defaultForFleet')}
                      />
                      {formData.appliesToAllVehicles && (
                        <FormHelperText>{t('fleetServiceTemplates.defaultForFleetHelp')}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>{t('fleetServiceTemplates.deleteItem')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('dialogs.deleteBody', { item: selectedTemplate?.templateName || t('fields.service') })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>{t('common.cancel')}</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isLoading}
            >{t('common.delete')}</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};
